import { useState, useEffect } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { PaymentsListTable } from "../components/payments/PaymentsListTable";
import { PaymentFiltersDialog } from "../components/payments/PaymentFiltersDialog";
import { PaymentStatusDialog } from "../components/payments/PaymentStatusDialog";
import { PayTabsRefundDialog } from "../components/payments/PayTabsRefundDialog";
import { paymentService } from "../services/paymentService";
import { Input } from "../components/ui/Input";
import { Card } from "../components/ui/Card";
import {
  Search,
  Filter,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  RotateCcw,
} from "lucide-react";
import { Button } from "../components/ui/Button";

interface PaymentStats {
  byStatus: Array<{ _id: string; count: number; totalAmount: number }>;
  byPaymentMethod: Array<{ _id: string; count: number; totalAmount: number }>;
  totalRevenue: number;
}

export default function PaymentsPage() {
  const [payments, setPayments] = useState<any[]>([]);
  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [filters, setFilters] = useState({
    status: "all",
    paymentMethod: "all",
  });

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<any>(null);
  const [refundingPayment, setRefundingPayment] = useState<any>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 10;

  const fetchStats = async () => {
    setIsStatsLoading(true);
    try {
      const response = await paymentService.getPaymentStats();
      setStats(response.data);
    } catch (err) {
      console.error("Failed to load payment stats:", err);
    } finally {
      setIsStatsLoading(false);
    }
  };

  const fetchPayments = async () => {
    setIsLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: pageSize,
      };

      if (filters.status !== "all") {
        params.status = filters.status;
      }

      if (filters.paymentMethod !== "all") {
        params.paymentMethod = filters.paymentMethod;
      }

      const response = await paymentService.getAdminPayments(params);
      setPayments(response.data || []);
      setTotalPages(response.totalPages || 1);
      setTotalItems(response.results || 0);
    } catch (err) {
      console.error("Failed to load payments:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [filters, currentPage]);

  const filteredPayments = payments.filter((item) => {
    const matchesSearch =
      (item.transactionId?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      ) ||
      (item.user?.username?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      ) ||
      (item.user?.email?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      );

    return matchesSearch;
  });

  const handleEdit = (item: any) => {
    setEditingPayment(item);
  };

  const handleDelete = async (item: any) => {
    if (
      confirm(
        "Are you sure you want to delete this payment? This cannot be undone."
      )
    ) {
      try {
        await paymentService.deleteAdminPayment(item._id);
        fetchPayments();
        fetchStats();
      } catch (err) {
        alert("Failed to delete payment");
      }
    }
  };

  const handleRefund = (item: any) => {
    setRefundingPayment(item);
  };

  const getStatByStatus = (statusId: string) => {
    const stat = stats?.byStatus?.find((s) => s._id === statusId);
    return stat
      ? { count: stat.count, amount: stat.totalAmount }
      : { count: 0, amount: 0 };
  };

  const completedStats = getStatByStatus("completed");
  const pendingStats = getStatByStatus("pending");
  const failedStats = getStatByStatus("failed");
  const refundedStats = getStatByStatus("refunded");

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">
            Payments
          </h2>
          <p className="text-gray-500">
            View and manage all transaction history.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          <Card className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">
                  {isStatsLoading
                    ? "..."
                    : `$${stats?.totalRevenue?.toLocaleString() || 0}`}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-gray-900">
                  {isStatsLoading ? "..." : completedStats.count}
                </p>
                <p className="text-xs text-gray-400">
                  ${completedStats.amount?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-full">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-gray-900">
                  {isStatsLoading ? "..." : pendingStats.count}
                </p>
                <p className="text-xs text-gray-400">
                  ${pendingStats.amount?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-full">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Failed</p>
                <p className="text-2xl font-bold text-gray-900">
                  {isStatsLoading ? "..." : failedStats.count}
                </p>
                <p className="text-xs text-gray-400">
                  ${failedStats.amount?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <RotateCcw className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Refunded</p>
                <p className="text-2xl font-bold text-gray-900">
                  {isStatsLoading ? "..." : refundedStats.count}
                </p>
                <p className="text-xs text-gray-400">
                  ${refundedStats.amount?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </Card>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="w-full sm:w-[408px]">
            <Input
              placeholder="Search by transaction ID, user..."
              icon={<Search size={20} />}
              className="bg-white"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto pb-2 sm:pb-0">
            <Button
              variant="outline"
              size="sm"
              className={`h-[39px] px-4 gap-2 ${
                Object.values(filters).some((v) => v !== "all")
                  ? "border-[#102040] text-[#102040] bg-blue-50"
                  : ""
              }`}
              onClick={() => setIsFilterOpen(true)}
            >
              <Filter size={16} />
              <span>Filters</span>
              {Object.values(filters).filter((v) => v !== "all").length > 0 && (
                <span className="flex items-center justify-center w-5 h-5 ml-1 text-[10px] font-bold text-white bg-[#102040] rounded-full">
                  {Object.values(filters).filter((v) => v !== "all").length}
                </span>
              )}
            </Button>
          </div>
        </div>

        <PaymentsListTable
          payments={filteredPayments}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRefund={handleRefund}
          totalItems={totalItems}
          currentPage={currentPage}
          pageSize={pageSize}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />

        <PaymentFiltersDialog
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={filters}
          onApply={(newFilters) => {
            setFilters(newFilters);
            setCurrentPage(1);
          }}
        />

        <PaymentStatusDialog
          isOpen={!!editingPayment}
          onClose={() => setEditingPayment(null)}
          onSuccess={() => {
            fetchPayments();
            fetchStats();
          }}
          payment={editingPayment}
        />

        {refundingPayment && (
          <PayTabsRefundDialog
            isOpen={!!refundingPayment}
            onClose={() => setRefundingPayment(null)}
            onSuccess={() => {
              fetchPayments();
              fetchStats();
              setRefundingPayment(null);
            }}
            orderId={refundingPayment.order?._id || refundingPayment.order || ''}
            amount={refundingPayment.amount}
            currency={refundingPayment.currency}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
